/**
 * Header › lupka (mobile) — otwiera warstwę Doofindera (fullscreen).
 *
 * API 2.x: `window.Doofinder.getRegistry('layer')` → `getTags()[0].open()`.
 * `doofinderApp()` to tylko kolejka startowa (przyjmuje config/init/disable),
 * więc `doofinderApp('show')` nic nie robi.
 *
 * Doofinder jest odroczony do pierwszej interakcji (mu-plugin hn-doofinder-lazy.php
 * + loader z hn-mailerlite-delay.php), więc przy pierwszym kliknięciu warstwy jeszcze
 * nie ma. Kolejność: pointerdown budzi skrypty i rozgrzewa tag → click otwiera.
 * Gdy nie zdąży (wolna sieć, blokada JS), fallback = rozwinięcie natywnego pola
 * search w headerze (`.hn-header--search-open`).
 */

// Pomiar 26.10 (Slow 4G, CPU 4x): tag warstwy gotowy ~2,3 s po tapnięciu, przy 2,5 s
// fallback otwierał się przed warstwą i lupka wyglądała na martwą do drugiego kliknięcia.
const DOOFINDER_WAIT_MS = 8000;
const LAYER_RENDER_MS = 2000;

let layerTag = null;   // rozgrzany tag — pozwala otworzyć warstwę synchronicznie
let warming = null;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Warstwa faktycznie się wyrenderowała? (bywa, że tag istnieje, a layer nie startuje) */
function isLayerVisible() {
    const root = document.querySelector('.dfd-root');
    return !!root && root.getBoundingClientRect().height > 0;
}

/** Loader odroczonych skryptów nasłuchuje m.in. `scroll` — syntetyczny event odpala go od razu. */
function wakeDelayedScripts() {
    if (document.querySelector('script[type="text/hn-mldelay"]')) {
        window.dispatchEvent(new Event('scroll'));
    }
}

/** Czeka na tag warstwy Doofindera i zapamiętuje go. Zwraca tag albo null. */
function warmLayerTag(timeout) {
    if (layerTag) return Promise.resolve(layerTag);
    if (warming) return warming;

    warming = (async () => {
        const deadline = Date.now() + timeout;
        try {
            while (!window.Doofinder && Date.now() < deadline) await sleep(50);
            if (!window.Doofinder) return null;

            await window.Doofinder.ready();
            const registry = await window.Doofinder.getRegistry('layer');
            let tags = registry.getTags();
            while (!tags.length && Date.now() < deadline) {
                await sleep(50);
                tags = registry.getTags();
            }
            layerTag = tags[0] || null;
        } catch (err) {
            layerTag = null;
        } finally {
            warming = null;
        }
        return layerTag;
    })();

    return warming;
}

/** Czeka chwilę na wyrenderowanie warstwy — inaczej lupka „nic nie robi". */
async function layerRendered() {
    const deadline = Date.now() + LAYER_RENDER_MS;
    while (Date.now() < deadline) {
        if (isLayerVisible()) return true;
        await sleep(60);
    }
    return false;
}

export function initSearchToggle() {
    const toggle = document.querySelector('[data-hn-search-toggle]');
    const header = document.querySelector('.hn-header');
    if (!toggle || !header) return;

    const input = header.querySelector('#hn-search-input, .hn-search__input');

    const setOpen = (open) => {
        header.classList.toggle('hn-header--search-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && input) input.focus();
    };

    // Start ładowania już na dotknięciu — click leci kilkadziesiąt ms później,
    // więc przy odrobinie szczęścia warstwa jest gotowa zanim palec puści ekran.
    toggle.addEventListener('pointerdown', () => {
        wakeDelayedScripts();
        warmLayerTag(DOOFINDER_WAIT_MS);
    }, { passive: true });

    toggle.addEventListener('click', async (e) => {
        e.preventDefault();
        if (toggle.getAttribute('aria-busy') === 'true') return;

        // Otwarty fallbackowy pasek — lupka go zamyka.
        if (toggle.getAttribute('aria-expanded') === 'true') {
            setOpen(false);
            return;
        }

        // Rozgrzany tag — otwieramy w tym samym gestcie (iOS pokazuje klawiaturę).
        if (layerTag) {
            layerTag.open();
            if (!(await layerRendered())) setOpen(true);
            return;
        }

        wakeDelayedScripts();
        toggle.setAttribute('aria-busy', 'true');
        const tag = await warmLayerTag(DOOFINDER_WAIT_MS);
        toggle.removeAttribute('aria-busy');

        if (tag) {
            tag.open();
            if (await layerRendered()) return;
        }
        setOpen(true);
    });

    initInlineSearchWarmup();

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || toggle.getAttribute('aria-expanded') !== 'true') return;
        setOpen(false);
        toggle.focus();
    });

    document.addEventListener('click', (e) => {
        if (toggle.getAttribute('aria-expanded') !== 'true') return;
        if (!header.contains(e.target)) setOpen(false);
    });
}

/**
 * Desktop: klik w pole budzi Doofindera, ale warstwa podpina się do pola, które już
 * ma fokus, więc sama się nie otwiera. Otwieramy ją, gdy tag jest gotowy.
 */
function initInlineSearchWarmup() {
    document.querySelectorAll('.hn-search__input').forEach((input) => {
        input.addEventListener('focus', async () => {
            if (layerTag) return;
            wakeDelayedScripts();
            const tag = await warmLayerTag(DOOFINDER_WAIT_MS);
            if (tag && document.activeElement === input && !isLayerVisible()) tag.open();
        });
    });
}
